<template>
  <div class="detalles-del-plan-page">
    <!-- Hero Section - Imagen 1.jpg con copy de marketing -->
    <section
      class="hero-section hero-animate"
      :style="{ backgroundImage: `url(${heroImage})` }"
    >
      <div class="container">
        <div class="row">
          <div class="col-lg-8 col-md-10">
            <h1 class="hero-title">
              Planes que protegen lo que más importa: tu salud y la de tu familia
            </h1>
            <p class="hero-subtitle">
              Accede a consultas médicas ilimitadas, telemedicina 24/7, descuentos en laboratorios y medicamentos. 
              Sin copagos, sin complicaciones. Miles de familias ya cuidan su bienestar con nosotros.
            </p>
            <router-link to="/contact" class="btn btn-hero-cta">
              Conoce tu plan ideal
              <i class="fas fa-arrow-right ml-2"></i>
            </router-link>
          </div>
        </div>
      </div>
    </section>

    <!-- Sección 3 planes con imágenes -->
    <section v-if="!loading && plans.length >= 3" class="plans-carousel-section">
      <div class="container">
        <div class="row align-items-stretch">
          <div
            v-for="(plan, index) in firstThreePlans"
            :key="plan.id"
            class="col-lg-4 col-md-6 mb-4 d-flex"
          >
            <div class="plan-image-card ddp-visible fade-in-up w-100" :style="`transition-delay: ${index * 0.08}s`">
              <div class="plan-image-top">
                <img :src="getPlanCarouselImage(index)" :alt="plan.name" class="plan-image" />
                <div class="plan-image-overlay">
                  <router-link to="/plans#planes-2027" class="btn btn-lo-necesito">Lo necesito ahora <i class="fas fa-arrow-right ml-1"></i></router-link>
                </div>
              </div>
              <div class="plan-card-body">
                <h3 class="plan-name">{{ plan.name }}</h3>
                <div class="plan-price-large" :class="{ 'price-secondary': index === 0 || index === 2 }">${{ plan.price }}/Mes</div>
                <div v-if="index === 0" class="plan-savings-line">
                  <span class="plan-original">$90</span>
                  <span class="plan-percent">36% off</span>
                </div>
                <div v-else-if="index === 2" class="plan-savings-line">
                  <span class="plan-original">$140</span>
                  <span class="plan-percent">34% off</span>
                </div>
                <div v-else class="plan-savings-spacer" aria-hidden="true">&nbsp;</div>
                <router-link to="/contact" class="btn btn-plan-available">
                  <img :src="disponibleIcon" alt="" class="btn-disponible-icon" />
                  Disponible
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ¿Por qué elegir? + Cuidado Integral y Preventivo -->
    <section class="cuidado-integral-section">
      <div class="container">
        <div class="section-header text-center mb-5 ddp-visible fade-in-up">
          <h2 class="section-title-main">¿Por qué elegir Hispano Medical Plan?</h2>
          <p class="section-subtitle-main">
            Porque tu salud merece lo mejor. Somos tu aliado de confianza, ofreciendo servicios médicos 
            de excelencia con la simplicidad y accesibilidad que buscas.
          </p>
        </div>
        <div class="cuidado-integral-block ddp-visible fade-in-down" style="transition-delay: 0.1s">
          <div class="row align-items-center">
            <div class="col-lg-8 col-md-7">
              <h3 class="cuidado-block-title">Cuidado Integral y Preventivo</h3>
              <p class="cuidado-block-text">
                Tu bienestar empieza con la prevención. Incluimos evaluaciones médicas completas, 
                chequeos preventivos y seguimiento continuo para que detectes cualquier situación a tiempo. 
                Cuidamos de ti hoy para que disfrutes una vida más saludable mañana.
              </p>
            </div>
            <div class="col-lg-4 col-md-5 text-center">
              <div class="cuidado-icon-wrapper">
                <img :src="cuidadoIcon" alt="Cuidado Integral y Preventivo" class="cuidado-icon" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ¿Por qué elegir Hispano Medical Plan? - Cards en columna izquierda, imagen derecha -->
    <section class="why-choose-section">
      <div class="container">
        <div class="row align-items-center">
          <div class="col-lg-7 col-md-6 order-2 order-lg-1">
            <div class="why-choose-cards-column">
              <div class="why-choose-card ddp-visible fade-in-up" v-for="(card, idx) in whyChooseCards" :key="idx" :style="`transition-delay: ${0.08 + idx * 0.06}s`">
                <div class="card-icon">
                  <img :src="card.icon" :alt="card.title" />
                </div>
                <h4 class="card-title">{{ card.title }}</h4>
                <p class="card-text">{{ card.text }}</p>
              </div>
            </div>
          </div>
          <div class="col-lg-5 col-md-6 order-1 order-lg-2 mb-4 mb-lg-0">
            <div class="why-choose-image-wrapper ddp-visible fade-in-left">
              <img :src="whyChooseImage" alt="Hispano Medical Plan" class="why-choose-image" />
              <router-link to="/plans" class="btn btn-ver-planes-overlay">
                Ver planes disponibles
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Nuestros planes - con imágenes 5, 6, 7 según plan -->
    <section v-if="!loading && plans.length > 0" class="nuestros-planes-section">
      <div class="nuestros-planes-header">
        <div class="container">
          <div class="section-header text-center ddp-visible fade-in-up">
            <h2 class="nuestros-planes-title">Nuestros planes</h2>
            <p class="nuestros-planes-subtitle">
              Elige el plan que mejor se adapte a ti y a tu familia
            </p>
          </div>
        </div>
      </div>
      <div class="container">
        <div
          v-for="(plan, index) in plans"
          :key="plan.id"
          class="plan-detail-block ddp-visible fade-in-up"
          :class="{ 'plan-block--image-right': index === 1 }"
          :style="`transition-delay: ${index * 0.08}s`"
        >
          <div class="plan-block-header text-center">
            <h3 class="plan-block-title">{{ plan.name }}</h3>
            <div class="plan-block-price">${{ plan.price }}/Mes</div>
          </div>
          <div class="plan-block-content">
            <div class="plan-block-image-col">
              <img :src="getPlanDetailImage(plan)" :alt="plan.name" class="plan-block-image" />
              <router-link to="/contact" class="btn btn-adquirir-plan">
                Adquirir plan {{ plan.name }}
              </router-link>
            </div>
            <div class="plan-block-benefits-col">
              <h4 class="benefits-title">Beneficios Principales</h4>
              <ul class="benefits-list" v-if="plan.parameters_details && plan.parameters_details.length > 0">
                <li v-for="(param, idx) in plan.parameters_details" :key="idx" class="benefit-row">
                  <img :src="benefitCheckIcon" alt="" class="benefit-check-img" />
                  <div class="benefit-item">
                    <div class="benefit-content">
                      <span class="benefit-name">{{ getBenefitName(param) }}</span>
                      <span class="benefit-detail" v-if="getBenefitDetail(param)">{{ getBenefitDetail(param) }}</span>
                    </div>
                  </div>
                </li>
              </ul>
              <p v-else class="no-benefits">Sin beneficios definidos para este plan.</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Tabla comparativa -->
    <section v-if="!loading" class="comparison-section">
      <div class="container">
        <div class="comparison-header ddp-visible fade-in-up">
          <div class="comparison-header-inner">
            <img :src="comparisonLogo" alt="" class="comparison-logo" />
            <div class="comparison-header-text">
              <h2 class="comparison-title">Comparación de planes</h2>
              <p class="comparison-subtitle">
                Comparación detallada de servicios médicos cubiertos
              </p>
            </div>
          </div>
        </div>
        <div class="comparison-row">
          <div class="comparison-col benefits-col ddp-visible fade-in-up" style="transition-delay: 0.1s">
            <div class="benefits-column-card">
              <table class="table plan-comparison-table">
                <thead>
                  <tr>
                    <th class="benefit-column">Beneficios Principales</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(benefit, bidx) in comparisonTableData.benefits" :key="'benefit-' + bidx">
                    <td class="benefit-cell">
                      <div class="benefit-cell-inner">
                        <img :src="benefitCheckIcon" alt="" class="benefit-check-table" />
                        <span>{{ benefit }}</span>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div
            v-for="(plan, pidx) in comparisonTableData.plans"
            :key="pidx"
            class="comparison-col plan-col ddp-visible fade-in-up"
            :style="`transition-delay: ${0.15 + pidx * 0.06}s`"
          >
            <div class="plan-comparison-card">
              <table class="table plan-comparison-table">
                <thead>
                  <tr>
                    <th class="plan-column">
                      <div class="plan-header-cell">
                        <span class="plan-header-label">PLAN MEDICO</span>
                        <span class="plan-header-name">({{ plan.name }})</span>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(value, vidx) in plan.values" :key="vidx">
                    <td class="plan-value-cell text-center">{{ value }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="plan-price-block">
              <span class="plan-price-value">${{ plan.price }},00</span>
              <span class="plan-price-period">/Mes</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Lo que dicen de nosotros -->
    <TestimonialsSection :testimonials="testimonials" :locations="locations" />

    <!-- Loading State -->
    <section v-if="loading" class="loading-section">
      <div class="container text-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="sr-only">Cargando...</span>
        </div>
        <p class="mt-3">Cargando planes...</p>
      </div>
    </section>
  </div>
</template>

<script>
import plansService from '@/core/services/api/landings/plans'
import { TestimonialsSection } from '../components'

/** Mapeo de imágenes para la sección de 3 planes (por índice) */
const CAROUSEL_IMAGES = [
  require('@/assets/lp/plans/1.png'),
  require('@/assets/lp/plans/2.png'),
  require('@/assets/lp/plans/3.png')
]

/** Mapeo de plan name (partial match) a imagen para Nuestros planes */
const PLAN_DETAIL_IMAGE_MAP = [
  { keywords: ['plus', 'tu salud plus'], image: require('@/assets/lp/plans/5.png') },
  { keywords: ['familiar', 'familia'], image: require('@/assets/lp/plans/7.png') },
  { keywords: ['tu salud', 'individual'], image: require('@/assets/lp/plans/6.png') }
]

export default {
  name: 'DetallesDelPlan',
  components: {
    TestimonialsSection
  },
  data() {
    return {
      plans: [],
      loading: true,
      heroImage: require('@/assets/lp/plans/1.jpg'),
      cuidadoIcon: require('@/assets/lp/plans/icons/Recurso 39@300x.png'),
      disponibleIcon: require('@/assets/lp/plans/icons/Recurso 25@300x.png'),
      benefitCheckIcon: require('@/assets/lp/plans/icons/Recurso 25@300x.png'),
      comparisonLogo: require('@/assets/images/logoLp.png'),
      whyChooseImage: require('@/assets/lp/2.png'),
      whyChooseCards: [
        {
          icon: require('@/assets/lp/Recurso 37@300x.png'),
          title: 'Red de Clínicas Afiliadas',
          text: 'Más de 50 clínicas esperándote. Donde estés, tenemos un profesional listo para cuidarte. Tu salud, a la vuelta de la esquina.'
        },
        {
          icon: require('@/assets/lp/Recurso 38@300x.png'),
          title: 'Ahorro en Medicamentos',
          text: 'Con ahorros de hasta 80%, recuperas el costo de tu plan en solo 2 meses. Tus medicamentos te esperan en tu clínica, sin filas.'
        },
        {
          icon: require('@/assets/lp/Recurso 39@300x.png'),
          title: 'Consultas por Video',
          text: 'Consulta con doctores certificados en minutos, desde tu celular. Sin citas, sin esperas. Tu salud, en tus manos, 24/7.'
        }
      ],
      testimonials: [
        { text: 'Gracias a Hispano Medical Plan, he podido llevar a mis hijos al médico sin preocuparme por los costos. El personal es amable y siempre nos atienden en español.', author: 'María Elena Martínez' },
        { text: 'Los descuentos en medicamentos han sido una gran ayuda para mi tratamiento. Ahorro hasta un 75% en mis medicamentos mensuales. Excelente servicio.', author: 'Carlos Rodríguez' },
        { text: 'La telemedicina ha cambiado mi vida. Puedo hablar con un médico a cualquier hora sin salir de casa. Recomiendo Hispano Medical Plan a toda mi familia.', author: 'Ana García' }
      ],
      /** Datos fijos para la tabla comparativa (no de BD) */
      comparisonTableData: {
        benefits: [
          'Consultas presenciales',
          'Video consulta',
          'Descuento en laboratorios',
          'Wellness Visit Anual',
          'Exámenes especiales',
          'Descuento en medicamentos',
          'Miembros incluidos',
          'Bienestar preventivo',
          'Soporte administrativo'
        ],
        plans: [
          {
            name: 'Tu salud/ Individual',
            price: '50',
            values: ['100% cubierto', '100% cubiertas', '20% cubierto', '1/año desde mes 3', 'No incluidos (20% desc.)', 'Hasta 80% ahorro', '1 miembro', 'Incluido', 'Incluido']
          },
          {
            name: 'Crónicas Avanzado',
            price: '90',
            values: ['100% cubierto', '100% cubiertas', '30% cubierto', '2/año + USG/X-ray', 'PAP/PSA (1/año desde mes 3)', 'Hasta 80% ahorro', '1 miembro', 'Incluido', 'Incluido']
          },
          {
            name: 'Familiar 2+1',
            price: '140',
            values: ['100% cubierto', '100% cubiertas', '30% cubierto', '1/año desde mes 3', 'PAP/PSA', 'Hasta 80% ahorro', '3 miembros', 'Incluido', 'Incluido']
          }
        ]
      },
      locations: [
        { name: 'Oak Cliff', address: '3434 W Illinois Ave Ste 202, Dallas, Texas, 75211', phone: '+12148889000' },
        { name: 'First Street Garland', address: '3050 S. First St, Garland, Texas, 75041', phone: '+12148889000' },
        { name: 'Fort Worth', address: '1303 N Beach St, Fort Worth, Texas, 76111', phone: '+12148889000' },
        { name: 'Garland', address: '3516 W Walnut St, Garland, Texas, 75042', phone: '+12148889000' },
        { name: 'Buckner Blvd Dallas', address: '3045 N Buckner Blvd Suite 110, Dallas, Texas, 75228', phone: '+12148889000' },
        { name: 'Coit', address: '14211 Coit Rd, Dallas, Texas, 75254', phone: '+12148889000' },
        { name: 'Denton', address: '231 W University Dr #111, Denton, Texas, 76201', phone: '+12148889000' },
        { name: 'Irving', address: '204 S Nursery Rd #166, Irving, Texas, 75060', phone: '+12148889000' },
        { name: 'Josey Lane', address: '3065 N Josey Ln Apt 28, Carrollton, Texas, 75007', phone: '+12148889000' },
        { name: 'McKinney', address: '811 N McDonald St, McKinney, Texas, 75069', phone: '+12148889000' }
      ]
    }
  },
  computed: {
    firstThreePlans() {
      return this.plans.slice(0, 3)
    },
    comparisonBenefits() {
      const seen = new Map()
      this.plans.forEach(plan => {
        if (!plan.parameters_details || !Array.isArray(plan.parameters_details)) return
        plan.parameters_details.forEach(param => {
          const name = this.getBenefitName(param)
          if (name && !seen.has(name)) {
            seen.set(name, { name, key: name })
          }
        })
      })
      return Array.from(seen.values())
    }
  },
  mounted() {
    this.loadPlans()
    this.setupScrollAnimations()
  },
  methods: {
    async loadPlans() {
      this.loading = true
      try {
        const response = await plansService.getAllPlans()
        let plansData = []
        if (response && response.status === 'ok' && response.data && Array.isArray(response.data)) {
          plansData = response.data
        } else if (Array.isArray(response)) {
          plansData = response
        } else if (response && response.data && Array.isArray(response.data)) {
          plansData = response.data
        }
        this.plans = plansData
      } catch (error) {
        console.error('Error al cargar planes:', error)
        this.plans = []
      } finally {
        this.loading = false
      }
    },
    getPlanCarouselImage(index) {
      return CAROUSEL_IMAGES[index] || CAROUSEL_IMAGES[0]
    },
    getPlanDetailImage(plan) {
      const name = (plan.name || '').toLowerCase()
      for (const { keywords, image } of PLAN_DETAIL_IMAGE_MAP) {
        if (keywords.some(k => name.includes(k))) return image
      }
      return require('@/assets/lp/plans/6.png')
    },
    getBenefitName(param) {
      return (param.parametrizable && param.parametrizable.name) ||
        (param.memberships_parameters_types && param.memberships_parameters_types.name) ||
        'Beneficio'
    },
    getBenefitDetail(param) {
      if (param.count != null && param.count !== '') {
        return `${param.count} incl.`
      }
      if (param.parametrizable && param.parametrizable.description) {
        return param.parametrizable.description
      }
      return ''
    },
    getPlanBenefitValue(plan, benefitKey) {
      if (!plan.parameters_details) return ''
      const param = plan.parameters_details.find(
        p => this.getBenefitName(p) === benefitKey
      )
      return param ? this.getBenefitDetail(param) || 'Incluido' : ''
    },
    setupScrollAnimations() {
      const observer = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible')
            }
          })
        },
        { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
      )
      const markVisibleIfInViewport = () => {
        const ddp = this.$el.querySelectorAll('.ddp-visible')
        const animated = this.$el.querySelectorAll('.animate-on-scroll')
        const elements = [...ddp, ...animated]
        elements.forEach(el => {
          observer.observe(el)
          const rect = el.getBoundingClientRect()
          const isInViewport = rect.top < window.innerHeight * 0.9 && rect.bottom > 0
          if (isInViewport) {
            setTimeout(() => el.classList.add('is-visible'), 100)
          }
        })
      }
      this.$nextTick(markVisibleIfInViewport)
      // Revisar de nuevo cuando termine el loading (algunas secciones se renderizan después)
      this.$watch('loading', (val) => {
        if (!val) this.$nextTick(markVisibleIfInViewport)
      })
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../styles/variables';

.detalles-del-plan-page {
  overflow-x: hidden;
}

/* Hero - sin overlay, imagen ya trae fondo azul */
.hero-section {
  min-height: 60vh;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  padding: 80px 0;
  color: white;
  display: flex;
  align-items: center;

  .hero-title {
    font-size: clamp(2.5rem, 6.5vw, 4.5rem);
    font-weight: 700;
    margin-bottom: 1.5rem;
    text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
    line-height: 1.2;
  }

  .hero-subtitle {
    font-size: clamp(1.15rem, 2.5vw, 1.75rem);
    margin-bottom: 2rem;
    opacity: 0.95;
    line-height: 1.6;
  }

  .btn-hero-cta {
    background: $landing-secondary-color;
    color: white;
    padding: 14px 32px;
    border-radius: 8px;
    font-weight: 700;
    font-size: 1.5rem;
    text-transform: uppercase;
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    transition: all 0.3s ease;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
    border: none;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(34, 207, 224, 0.4);
      background: darken($landing-secondary-color, 8%);
      color: white;
      text-decoration: none;
    }
  }

  @media (max-width: 768px) {
    min-height: 50vh;
    padding: 60px 1rem;
  }
}

/* Sección 3 planes */
.plans-carousel-section {
  padding: 80px 0;
  background: $landing-bg-light;

  .plan-image-card {
    background: transparent;
    border-radius: 15px;
    overflow: hidden;
    box-shadow: none;
    transition: all 0.3s ease;
    height: 100%;
    display: flex;
    flex-direction: column;

    &:hover {
      transform: translateY(-4px);
    }

    .plan-image-top {
      position: relative;
      aspect-ratio: 3/4;
      overflow: hidden;
      border-radius: 15px 15px 0 0;

      .plan-image {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
      }

      .plan-image-overlay {
        position: absolute;
        bottom: 1rem;
        left: 0;
        right: 0;
        display: flex;
        justify-content: center;
      }

      .btn-lo-necesito {
        background: white;
        color: $landing-primary-color;
        border: 2px solid $landing-primary-color;
        padding: 12px 24px;
        border-radius: 8px;
        font-weight: 600;
        font-size: 1.15rem;
        text-transform: uppercase;
        text-decoration: none;
        transition: all 0.3s ease;

        &:hover {
          background: $landing-primary-color;
          color: white;
          text-decoration: none;
        }
      }
    }

    .plan-card-body {
      padding: 1.25rem 1.5rem;
      text-align: center;
      display: flex;
      flex-direction: column;
      align-items: center;
      flex: 1;
      min-height: 0;
    }

    .plan-name {
      font-size: 1.75rem;
      font-weight: 600;
      color: $landing-text-dark;
      margin: 0 0 0.5rem;
    }

    .plan-price-large {
      font-size: 4rem;
      font-weight: 700;
      color: $landing-primary-color;
      margin-bottom: 0.25rem;

      &.price-secondary {
        color: $landing-secondary-color;
      }
    }

    .plan-savings-line {
      margin-bottom: 0.5rem;
      font-size: 1.6rem;

      .plan-original {
        color: $landing-primary-color;
        text-decoration: line-through;
        margin-right: 0.5rem;
      }

      .plan-percent {
        color: $landing-primary-color;
      }
    }

    .plan-savings-spacer {
      visibility: hidden;
      font-size: 1.6rem;
      line-height: 1.5;
      margin-bottom: 0.5rem;
      min-height: 2.4em;
    }

    .btn-plan-available {
      margin-top: auto;
      padding-top: 1rem;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      background: white;
      color: $landing-primary-color;
      border: 1px solid $landing-border-light;
      padding: 12px 28px;
      border-radius: 8px;
      font-weight: 600;
      font-size: 1.2rem;
      text-transform: uppercase;
      text-decoration: none;
      transition: all 0.3s ease;

      .btn-disponible-icon {
        width: 24px;
        height: 24px;
        object-fit: contain;
      }

      &:hover {
        background: $landing-bg-light;
        color: $landing-primary-color;
        border-color: $landing-primary-color;
        text-decoration: none;
      }
    }
  }
}

/* ¿Por qué elegir? + Cuidado Integral */
.cuidado-integral-section {
  padding: 80px 0;
  background: white;

  .section-title-main {
    font-size: clamp(2.2rem, 5.5vw, 3.5rem);
    font-weight: 700;
    color: $landing-secondary-color;
    margin-bottom: 1rem;
  }

  .section-subtitle-main {
    font-size: 1.6rem;
    color: $landing-secondary-color;
    line-height: 1.7;
    margin: 0;
    max-width: 750px;
    margin-left: auto;
    margin-right: auto;
  }

  .cuidado-integral-block {
    background: $landing-primary-color;
    border-radius: 15px;
    padding: 3rem 2.5rem;
  }

  .cuidado-block-title {
    font-size: clamp(2rem, 4vw, 3.2rem);
    font-weight: 700;
    color: white;
    margin-bottom: 1rem;
  }

  .cuidado-block-text {
    font-size: 1.5rem;
    color: white;
    line-height: 1.8;
    margin: 0;
    opacity: 0.95;
  }

  .cuidado-icon-wrapper {
    .cuidado-icon {
      max-width: 200px;
      height: auto;
    }
  }
}

/* ¿Por qué elegir? - igual que Home benefits-section */
.why-choose-section {
  padding: 100px 0;
  background: linear-gradient(180deg, #F3F6F9 0%, #ffffff 50%, #F3F6F9 100%);

  .why-choose-image-wrapper {
    position: relative;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;

    .why-choose-image {
      width: 100%;
      height: auto;
      object-fit: contain;
    }

    .btn-ver-planes-overlay {
      position: absolute;
      bottom: 1.5rem;
      left: 50%;
      transform: translateX(-50%);
      background: linear-gradient(135deg, $landing-secondary-color 0%, $landing-secondary-color 65%, $landing-primary-color 100%);
      color: white;
      border: none;
      padding: 16px 48px;
      min-width: 280px;
      border-radius: 10px;
      font-weight: 700;
      font-size: 1.5rem;
      text-transform: uppercase;
      text-align: center;
      text-decoration: none;
      transition: all 0.3s ease;

      &:hover {
        background: linear-gradient(135deg, darken($landing-secondary-color, 5%) 0%, darken($landing-secondary-color, 5%) 65%, $landing-primary-color 100%);
        color: white;
        text-decoration: none;
        transform: translate(-50%, -2px);
      }
    }
  }

  .why-choose-cards-column {
    display: flex;
    flex-direction: column;
    gap: 2rem;
  }

  .why-choose-card {
    background: white;
    border-radius: 15px;
    padding: 2.5rem;
    border: 2px solid $landing-border-light;
    transition: all 0.3s ease;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;

    &:hover {
      transform: translateY(-5px);
      box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);

      .card-icon img {
        transform: scale(1.05);
      }
    }

    .card-icon {
      width: 80px;
      height: 80px;
      background: transparent;
      border: none;
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 1.5rem;

      img {
        width: 100%;
        height: 100%;
        object-fit: contain;
        transition: transform 0.3s ease;
      }
    }

    .card-title {
      font-size: 1.75rem;
      font-weight: 700;
      color: $landing-primary-color;
      margin: 0 0 1.25rem;
      line-height: 1.3;
      width: 100%;
    }

    .card-text {
      font-size: 1.1rem;
      color: $landing-text-dark;
      line-height: 1.7;
      margin: 0;
      font-weight: 400;
      width: 100%;
    }
  }
}

/* Nuestros planes */
.nuestros-planes-section {
  padding: 0 0 80px;
  background: white;

  .nuestros-planes-header {
    background: $landing-primary-color;
    padding: 4rem 0;
    margin-bottom: 3rem;
  }

  .nuestros-planes-title {
    font-size: clamp(2.5rem, 6vw, 4.5rem);
    font-weight: 700;
    color: white;
    margin: 0 0 1rem;
    line-height: 1.2;
  }

  .nuestros-planes-subtitle {
    font-size: clamp(1.4rem, 3vw, 2rem);
    color: white;
    margin: 0;
    line-height: 1.5;
    opacity: 0.95;
  }

  /* Bloque de plan: título arriba, imagen izquierda, beneficios derecha */
  .plan-detail-block {
    margin-bottom: 4rem;

    &:last-child {
      margin-bottom: 0;
    }

    &.plan-block--image-right .plan-block-content {
      flex-direction: row-reverse;
    }

    .plan-block-header {
      margin-bottom: 1.5rem;
    }

    .plan-block-title {
      font-size: clamp(1.75rem, 4vw, 2.5rem);
      font-weight: 700;
      color: $landing-text-dark;
      margin: 0 0 0.25rem;
      text-transform: uppercase;
      letter-spacing: 0.02em;
    }

    .plan-block-price {
      font-size: clamp(2rem, 4vw, 3rem);
      font-weight: 700;
      color: $landing-primary-color;
      margin: 0;

      &::after {
        content: '';
        display: block;
      }
    }

    .plan-block-content {
      display: flex;
      flex-wrap: wrap;
      gap: 2rem;
      align-items: flex-start;
    }

    .plan-block-image-col {
      flex: 0 0 48%;
      min-width: 280px;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 1.25rem;

      .plan-block-image {
        width: 100%;
        height: auto;
        aspect-ratio: 3/4;
        object-fit: contain;
        border-radius: 10px;
      }

      .btn-adquirir-plan {
        width: 100%;
        background: linear-gradient(135deg, $landing-secondary-color 60%, $landing-primary-color 100%);
        color: white;
        padding: 14px 28px;
        border-radius: 10px;
        font-weight: 600;
        text-align: center;
        text-decoration: none;
        text-transform: uppercase;
        letter-spacing: 0.03em;
        transition: all 0.3s ease;

        &:hover {
          background: linear-gradient(135deg, darken($landing-secondary-color, 5%) 60%, $landing-primary-color 100%);
          color: white;
          text-decoration: none;
          transform: translateY(-2px);
        }
      }
    }

    .plan-block-benefits-col {
      flex: 1;
      min-width: 260px;
      display: flex;
      flex-direction: column;
      align-items: center;
    }

    .benefits-title {
      font-size: clamp(1.5rem, 3vw, 2rem);
      font-weight: 700;
      color: $landing-text-dark;
      margin: 0 0 1.25rem;
      width: 100%;
      text-align: center;
    }

    .benefits-list {
      list-style: none;
      padding: 0;
      margin: 0;
      width: 100%;
      max-width: 480px;
    }

    .benefit-row {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      margin-bottom: 0.5rem;

      &:last-child {
        margin-bottom: 0;
      }
    }

    .benefit-check-img {
      width: 1.5rem;
      height: 1.5rem;
      flex-shrink: 0;
    }

    .benefit-item {
      flex: 1;
      padding: 0.75rem 1rem;
      background: white;
      border: 1px solid $landing-border-light;
      border-radius: 8px;
      box-shadow: 0 2px 8px $landing-primary-rgba-08;
      transition: border-color 0.3s ease;

      &:hover {
        border-color: $landing-primary-color;
      }
    }

    .benefit-content {
      display: flex;
      flex-direction: column;
      gap: 0.2rem;
      text-align: center;
    }

    .benefit-name {
      font-weight: 600;
      color: $landing-secondary-color;
      font-size: 1.15rem;
      text-transform: uppercase;
    }

    .benefit-detail {
      font-size: 1rem;
      color: $landing-secondary-rgba-70;
      text-transform: uppercase;
    }

    .no-benefits {
      color: $landing-text-medium;
      font-size: 1rem;
      margin: 0;
    }
  }
}

/* Tabla comparativa */
.comparison-section {
  padding: 80px 0;
  background: white;

  .comparison-header {
    margin-bottom: 4rem;

    .comparison-header-inner {
      display: flex;
      align-items: center;
      gap: 1.25rem;
      flex-wrap: wrap;
      justify-content: center;
    }

    .comparison-logo {
      height: 56px;
      width: auto;
      object-fit: contain;
    }

    .comparison-title {
      font-size: clamp(2rem, 5vw, 3rem);
      font-weight: 700;
      color: $landing-primary-color;
      margin: 0 0 0.35rem;
      line-height: 1.2;
      letter-spacing: 0.08em;
    }

    .comparison-subtitle {
      font-size: 1.15rem;
      color: $landing-text-medium;
      margin: 0;
      letter-spacing: 0.02em;
    }
  }

  .comparison-row {
    display: flex;
    flex-wrap: nowrap;
    justify-content: center;
    gap: 1rem;
    overflow-x: auto;
    align-items: flex-start;
  }

  .comparison-col {
    display: flex;
    flex-direction: column;
    gap: 0;
  }

  .benefits-col {
    flex: 0 0 auto;
    min-width: 340px;
  }

  .plan-col {
    flex: 0 0 auto;
    min-width: 220px;
  }

  .benefits-column-card {
    flex: 0 0 auto;
    min-width: 340px;
    overflow: hidden;
    box-shadow: 0 4px 20px $landing-primary-rgba-08;
    border-radius: 12px;
    border: 1px solid $landing-border-light;
  }

  .plan-comparison-card {
    flex: 0 0 auto;
    min-width: 220px;
    overflow: hidden;
    box-shadow: 0 4px 20px $landing-primary-rgba-08;
    border-radius: 12px;
    border: 1px solid $landing-border-light;
  }

  .plan-comparison-table {
    width: 100%;
    background: white;

    thead th {
      min-height: 110px;
      padding: 1.5rem 1.25rem 2.5rem;
      vertical-align: top;
    }

    thead .benefit-column {
      min-height: 110px;
      padding: 1.5rem 1.25rem 4rem;
    }

    thead {
      background: $landing-primary-color;

      th {
        border: none;
        font-weight: 600;
        color: white;
        text-align: center;
      }

      .benefit-column {
        text-align: center;
        font-size: 1.3rem;
        text-transform: uppercase;
      }

      .plan-header-cell {
        display: flex;
        flex-direction: column;
        gap: 0.2rem;

        .plan-header-label {
          font-size: 1.1rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          color: white;
        }

        .plan-header-name {
          font-size: 1rem;
          font-weight: 600;
          letter-spacing: 0.02em;
          color: white;
        }
      }
    }

    tbody {
      td {
        padding: 0.9rem 1rem;
        vertical-align: middle;
        border-bottom: 2px solid $landing-primary-color;
        color: $landing-secondary-color;
        font-size: 1rem;
        font-weight: 600;
      }

      tr:last-child td {
        border-bottom: 2px solid $landing-primary-color;
      }

      .benefit-cell .benefit-cell-inner {
        display: flex;
        align-items: center;
        gap: 0.6rem;
      }

      .benefit-check-table {
        width: 1.25rem;
        height: 1.25rem;
        flex-shrink: 0;
      }

      .plan-value-cell {
        text-align: center;
      }
    }
  }

  .plan-col .plan-price-block {
    margin-top: 1.5rem;
    background: $landing-primary-color;
    color: white;
    padding: 2rem 1.5rem;
    text-align: center;
    border-radius: 12px;

    .plan-price-value {
      font-size: 2.5rem;
      font-weight: 700;
      display: block;
      line-height: 1.1;
    }

    .plan-price-period {
      font-size: 1.4rem;
      opacity: 0.95;
      margin-top: 0.2rem;
    }
  }
}

/* Animaciones sutiles de subida/bajada al hacer scroll */
.ddp-visible {
  opacity: 0;
  transition: opacity 0.6s cubic-bezier(0.4, 0, 0.2, 1),
              transform 0.6s cubic-bezier(0.4, 0, 0.2, 1);

  &.fade-in-up {
    transform: translateY(18px);

    &.is-visible {
      opacity: 1;
      transform: translateY(0);
    }
  }

  &.fade-in-down {
    transform: translateY(-18px);

    &.is-visible {
      opacity: 1;
      transform: translateY(0);
    }
  }

  &.fade-in-left {
    transform: translateX(-18px);

    &.is-visible {
      opacity: 1;
      transform: translateX(0);
    }
  }

  &.is-visible {
    opacity: 1;
    transform: translate(0);
  }
}

@media (max-width: 991px) {
  .plans-carousel-section,
  .cuidado-integral-section,
  .why-choose-section,
  .nuestros-planes-section,
  .comparison-section {
    padding: 60px 0;
  }

  .plan-detail-block .plan-block-content {
    flex-direction: column;
  }

  .plan-detail-block.plan-block--image-right .plan-block-content {
    flex-direction: column;
  }

  .plan-detail-block .plan-block-image-col {
    flex: 1 1 100%;
    width: 100%;
    align-items: center;

    .plan-block-image {
      max-width: 100%;
    }

    .btn-adquirir-plan {
      max-width: 100%;
    }
  }

  .comparison-row {
    flex-wrap: wrap;
  }

  .benefits-col,
  .plan-col {
    min-width: 100%;
  }

  .benefits-column-card,
  .plan-comparison-card {
    min-width: 100%;
  }

  .plan-comparison-table {
    font-size: 0.9rem;
  }
}

@media (max-width: 768px) {
  .hero-section {
    .hero-title {
      font-size: 2rem;
    }

    .hero-subtitle {
      font-size: 1.15rem;
    }
  }

  .plans-carousel-section .plan-image-card .plan-name {
    font-size: 1.2rem;
  }
}
</style>
